import { RadioCardGroup } from "@blurb/codex-react";
import { Box, createStyles, Stack } from "@mantine/core";
import { RpiAlert, RpiText } from "@rpi/openapi-core";
import {
  attributesForFiltering,
  invalidateSubsequentSteps,
  NewProductState,
  ProductCreateNavButtons,
  StepProps,
} from "./ProductCreateNavigator";
import {
  filterByMatchingAttributes,
  findAvailableAttributeCombinations,
  ProductAttributes,
  serializeCombination,
} from "../../hooks/useStandardProducts";
import { formatRestrictingStepsSentence, getRestrictingOptions, sortDisabledLast } from "./attributeHelpers";
import { ReactNode, useState } from "react";

// Arguments ChoiceStep passes to `renderCard` in "disable" mode. Step authors thread them straight through to
// their `TextCard`/`IconCard`; they do not compute availability, sort, or tooltip wording themselves.
export type DisableRenderCardArgs = {
  disabled: boolean;
  renderTooltip?: () => JSX.Element;
};

type ChoiceStepPropsBase = StepProps & {
  instructionText?: ReactNode;
  noOptionsText?: string;
  flexDirection?: "row" | "column";
  isLoading?: boolean;
  renderImage?: (combination: ProductAttributes, id: string) => JSX.Element | null;
  onAdditionalValidation?: () => Promise<ReactNode | undefined>;
};

/**
 * How a step treats values unavailable for the user's current upstream selections. Required, with no mode that
 * skips both:
 * - `"hide"` — enumerate only the combinations reachable from the current selections.
 * - `"disable"` — enumerate every combination the step owns; ChoiceStep sorts the unreachable ones last and hands
 *   `renderCard` a `{ disabled, renderTooltip }`, owning the tooltip sentence that names the restricting steps.
 */
type ChoiceStepProps = ChoiceStepPropsBase &
  (
    | {
        restrictedValues: "hide";
        renderCard: (combination: ProductAttributes, id: string) => JSX.Element | null;
        // "hide" mode has nothing to disable, so the tooltip suffix hook is not accepted.
        getTooltipSuffix?: never;
      }
    | {
        restrictedValues: "disable";
        renderCard: (combination: ProductAttributes, id: string, args: DisableRenderCardArgs) => JSX.Element | null;
        // Optional per-value tooltip suffix. Rendered after the shared sentence and a blank line. Intended for
        // narrow value-specific additions (e.g. the Interior Color step's Black & White CMYK addendum); it is
        // strictly additive — step authors cannot override the shared sentence.
        getTooltipSuffix?: (combination: ProductAttributes) => ReactNode;
      }
  );

const useCardStepStyles = createStyles((theme, { flexDirection }: { flexDirection: "row" | "column" }) => ({
  cardGrid: {
    display: "flex",
    gap: "var(--codex-spacing-2)",
    marginTop: "var(--codex-spacing-8)",
    marginBottom: "var(--codex-spacing-2)",
    flexDirection,
    flexWrap: "wrap",
  },
}));

// Generic card step component. A step owns one or more attributes; each card is a distinct combination of their values.
export function ChoiceStep(props: ChoiceStepProps) {
  const {
    state,
    setState,
    attributeKeys,
    instructionText = "Please select one to continue.",
    noOptionsText = "No options available based on previous selections.",
    standardProducts,
    visibleSteps,
    renderImage,
    flexDirection = "row",
    onAdditionalValidation,
    isLoading,
  } = props;
  const { classes } = useCardStepStyles({ flexDirection });
  const [errorMessage, setErrorMessage] = useState<ReactNode>("");

  if (attributeKeys.length === 0) return <RpiAlert>Configuration error: No attribute keys.</RpiAlert>;

  // Combinations reachable from the current upstream selections — the enabled set. Used to decide `disabled`
  // in "disable" mode and to enumerate directly in "hide" mode.
  const upstreamProducts = filterByMatchingAttributes(standardProducts, attributesForFiltering(state, visibleSteps));
  const availableCombinations = findAvailableAttributeCombinations(upstreamProducts, attributeKeys);
  const availableIds = new Set(availableCombinations.map((c) => serializeCombination(attributeKeys, c)));

  // "hide" shows only reachable combinations; "disable" enumerates every catalog combination for the step and
  // marks the unreachable ones disabled (sorted after the reachable ones).
  const enumeratedCombinations =
    props.restrictedValues === "hide"
      ? availableCombinations
      : findAvailableAttributeCombinations(standardProducts, attributeKeys);
  const sortedCombinations =
    props.restrictedValues === "hide"
      ? enumeratedCombinations
      : sortDisabledLast(enumeratedCombinations, (c) => !availableIds.has(serializeCombination(attributeKeys, c)));

  const currentCombination: ProductAttributes = Object.fromEntries(
    attributeKeys.map((key) => [key, state.attributes[key]])
  );
  const currentValue = attributeKeys.every((key) => state.attributes[key] !== undefined)
    ? serializeCombination(attributeKeys, currentCombination)
    : "";

  const handleSelect = (id: string) => {
    // In "disable" mode disabled cards are still rendered; guard against selecting one.
    if (!availableIds.has(id)) return;
    const selected = availableCombinations.find(
      (combination) => serializeCombination(attributeKeys, combination) === id
    );
    if (!selected) return;
    setState((draft) => {
      for (const key of attributeKeys) {
        draft.attributes[key] = selected[key];
      }
      draft.nextFailed = false;
      invalidateSubsequentSteps(draft, draft.currentStepIndex, visibleSteps);
    });
  };

  const handleNext = async () => {
    const validationError = validateSelection(attributeKeys, state) || (await onAdditionalValidation?.());

    if (validationError) {
      setErrorMessage(validationError);
      setState((draft) => {
        draft.nextFailed = true;
      });
    } else {
      setErrorMessage("");
      setState((draft) => {
        draft.completedStepIndex = draft.currentStepIndex;
        draft.currentStepIndex += 1;
        draft.nextFailed = false;
      });
    }
  };

  // Tooltip context for "disable" mode. Candidate steps are the earlier visible steps — those the user has already
  // committed to; `getRestrictingOptions` returns the subset that restricts the disabled value.
  const currentStepConfig = visibleSteps[state.currentStepIndex];
  const currentStepTooltipLabel = currentStepConfig?.tooltipLabel ?? "";
  const candidateSteps = visibleSteps.slice(0, state.currentStepIndex);

  const buildDisableArgs = (combination: ProductAttributes, id: string): DisableRenderCardArgs => {
    const disabled = !availableIds.has(id);
    if (!disabled || props.restrictedValues !== "disable") return { disabled };

    const restrictingSteps = getRestrictingOptions({
      standardProducts,
      currentSelections: state.attributes,
      targetAttributes: attributeKeys,
      targetValues: combination,
      candidateSteps,
    });
    const restrictorLabels = restrictingSteps.map((step) => step.tooltipLabel);
    const sentence = formatRestrictingStepsSentence(currentStepTooltipLabel, restrictorLabels);
    if (!sentence) return { disabled: true };

    const suffix = props.getTooltipSuffix?.(combination);
    const renderTooltip = () => (
      <>
        {sentence}
        {suffix !== undefined && suffix !== null ? (
          <>
            <br />
            <br />
            {suffix}
          </>
        ) : null}
      </>
    );
    return { disabled: true, renderTooltip };
  };

  return (
    <Stack spacing={0}>
      <RpiText>{instructionText}</RpiText>

      {enumeratedCombinations.length === 0 ? (
        <RpiAlert>{noOptionsText}</RpiAlert>
      ) : (
        <RadioCardGroup value={currentValue} onValueChange={(value) => handleSelect(String(value))}>
          <Box className={classes.cardGrid}>
            {sortedCombinations.map((combination) => {
              const id = serializeCombination(attributeKeys, combination);
              if (props.restrictedValues === "hide") {
                return props.renderCard(combination, id);
              }
              return props.renderCard(combination, id, buildDisableArgs(combination, id));
            })}
          </Box>
        </RadioCardGroup>
      )}

      <Box sx={{ marginBottom: "var(--codex-spacing-6)" }}>
        {currentValue && renderImage && renderImage(currentCombination, currentValue)}
      </Box>
      {state.nextFailed && errorMessage && <RpiAlert>{errorMessage}</RpiAlert>}

      <ProductCreateNavButtons
        state={state}
        setState={setState}
        visibleSteps={visibleSteps}
        onNext={handleNext}
        loading={isLoading}
      />
    </Stack>
  );
}

const noSelectionErrorText = "Please make a selection to continue.";
function validateSelection(attributeKeys: string[], state: NewProductState) {
  if (attributeKeys.some((key) => state.attributes[key] === undefined)) {
    return noSelectionErrorText;
  }
}
